import React, { useState } from "react";

/**
 * Nekobiz-style workspace shell: left sidebar (brand, sectioned nav,
 * foot) + main column (header bar with title/subtitle/actions, content).
 * On small screens the sidebar becomes a drawer toggled from the header.
 *
 * sections: [{ label?, items: [{ id, icon, label, badge? }] }]
 */
export default function BookingShell({
    brand, sub, sections, active, onNav,
    title, subtitle, actions, foot, children,
}) {
    const [drawer, setDrawer] = useState(false);

    const go = (id) => {
        onNav(id);
        setDrawer(false);
    };

    const sidebar = (
        <>
            <div className="h-[70px] flex items-center px-6 border-b border-[#e2e8f0] shrink-0">
                <div className="flex items-center gap-2.5 min-w-0">
                    <span className="w-9 h-9 rounded-lg bg-[#0f172a] text-white flex items-center justify-center text-base shrink-0">
                        ⚡
                    </span>
                    <div className="min-w-0">
                        <div className="text-[18px] font-bold leading-tight truncate">{brand}</div>
                        {sub && <div className="text-[11px] text-[#94a3b8]">{sub}</div>}
                    </div>
                </div>
            </div>
            <nav className="px-3 py-4 flex-1 overflow-y-auto">
                {sections.map((sec, i) => (
                    <div key={i}>
                        {sec.label && (
                            <div className="text-[10px] font-bold tracking-[0.08em] uppercase text-[#94a3b8] px-[13px] pt-[14px] pb-[6px]">
                                {sec.label}
                            </div>
                        )}
                        {sec.items.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => go(item.id)}
                                className={`w-full flex items-center gap-3 px-[13px] py-[11px] mb-[3px] rounded-lg text-sm transition ${active === item.id
                                        ? "bg-[#0f172a] text-white font-medium"
                                        : "text-[#64748b] hover:bg-[#f8fafc] hover:text-[#0f172a]"
                                    }`}
                            >
                                <span className="w-[18px] text-center shrink-0">{item.icon}</span>
                                <span className="flex-1 text-left truncate">{item.label}</span>
                                {item.badge !== undefined && item.badge > 0 && (
                                    <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${active === item.id ? "bg-white/20 text-white" : "bg-[#f1f5f9] text-[#475569]"}`}>
                                        {item.badge}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                ))}
            </nav>
            {foot && (
                <div className="p-3 border-t border-[#e2e8f0] grid gap-2">
                    {foot}
                </div>
            )}
        </>
    );

    return (
        <div className="flex bg-white border-y border-[#e2e8f0]" style={{ minHeight: "calc(100vh - 200px)" }}>
            {/* desktop sidebar */}
            <aside className="w-60 bg-white border-r border-[#e2e8f0] hidden md:flex flex-col shrink-0 md:sticky md:top-16 md:h-[calc(100vh-4rem)]">
                {sidebar}
            </aside>

            {/* mobile drawer */}
            {drawer && (
                <div className="fixed inset-0 z-50 md:hidden">
                    <div className="absolute inset-0 bg-[rgba(15,23,42,.45)]" onClick={() => setDrawer(false)} />
                    <aside className="absolute left-0 top-0 bottom-0 w-[270px] max-w-[85vw] bg-white shadow-2xl flex flex-col">
                        {sidebar}
                    </aside>
                </div>
            )}

            {/* main */}
            <main className="flex-1 min-w-0 flex flex-col bg-[#f8fafc]">
                <header className="min-h-[70px] bg-white border-b border-[#e2e8f0] flex items-center justify-between gap-3 px-4 sm:px-6 py-3 flex-wrap shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <button
                            className="md:hidden hk-btn hk-btn-outline hk-btn-sm shrink-0"
                            onClick={() => setDrawer(true)}
                            aria-label="Open navigation"
                        >
                            ☰
                        </button>
                        <div className="min-w-0">
                            <div className="text-xl font-bold truncate">{title}</div>
                            {subtitle && <div className="text-xs text-[#64748b] mt-0.5 truncate">{subtitle}</div>}
                        </div>
                    </div>
                    {actions && (
                        <div className="flex items-center gap-2 flex-wrap">
                            {actions}
                        </div>
                    )}
                </header>
                <div className="flex-1 min-w-0">
                    <div className="max-w-[1400px] mx-auto w-full p-3 sm:p-6">
                        {children}
                    </div>
                </div>
            </main>
        </div>
    );
}
